// frontend/src/pages/staff/StaffProfile.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import StaffSidebarLayout from "./StaffSidebarLayout";
import ServiceRequestModal from "../../components/ServiceRequestModal";
import ShareProfileButton from "../../components/ShareProfileButton";
import axios from "axios";
import "./staffStyles.css";

function StaffProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [profileData, setProfileData] = useState(null);
  const [services, setServices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [showRequestModal, setShowRequestModal] = useState(false);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Always use 'professional' role type
  const roleType = "professional";

  // Get role label - always show "Professional"
  const getRoleLabel = useCallback(() => {
    return "Professional";
  }, []);

  // Use unified professional dashboard path
  const basePath = "/dashboard/professional";

  // Security: Get auth headers with validation
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error. Please contact support.");
      return null;
    }
    return baseURL;
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      // Fetch profile and services in parallel
      const [profileRes, servicesRes] = await Promise.all([
        axios.get(`${baseURL}/api/staff/me`, { headers }),
        axios.get(`${baseURL}/api/staff/me/services`, { headers }),
      ]);

      setProfileData(profileRes.data);
      setServices(servicesRes.data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg("Unable to load profile. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl, navigate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleEditClick = () => {
    navigate(`${basePath}/profile/edit`);
  };

  const roleLabel = getRoleLabel();

  if (loading) {
    return (
      <StaffSidebarLayout roleType={roleType}>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading profile...</p>
        </div>
      </StaffSidebarLayout>
    );
  }

  if (errorMsg) {
    return (
      <StaffSidebarLayout roleType={roleType}>
        <div style={{ padding: "40px" }}>
          <div
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "20px",
            }}
          >
            <h3 style={{ color: "#c00", margin: "0 0 10px 0" }}>Error</h3>
            <p style={{ margin: 0 }}>{errorMsg}</p>
            <button
              className="btn-primary"
              style={{ marginTop: "15px" }}
              onClick={fetchProfile}
            >
              Retry
            </button>
          </div>
        </div>
      </StaffSidebarLayout>
    );
  }

  if (!profileData) {
    return (
      <StaffSidebarLayout roleType={roleType}>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Profile not found</p>
        </div>
      </StaffSidebarLayout>
    );
  }

  return (
    <StaffSidebarLayout roleType={roleType}>
      <div
        style={{
          padding: isMobile ? "12px" : "24px",
          maxWidth: "100%",
        }}
      >
        {/* Header Bar */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            marginBottom: "24px",
            gap: isMobile ? "12px" : "16px",
          }}
        >
          <button
            onClick={() => navigate(basePath)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 18px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              background: "#fff",
              color: "#333",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s",
              width: isMobile ? "100%" : "auto",
              justifyContent: isMobile ? "center" : "flex-start",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#f5f5f5";
              e.target.style.borderColor = "#001d4a";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#fff";
              e.target.style.borderColor = "#ddd";
            }}
          >
            <span>←</span> Back to Dashboard
          </button>
          <button
            className="btn-primary"
            onClick={handleEditClick}
            style={{
              fontSize: "14px",
              padding: "10px 24px",
              width: isMobile ? "100%" : "auto",
            }}
          >
            ✏️ Edit Profile
          </button>
        </div>

        {/* Share Profile Link */}
        {profileData.staff?.slug &&
          (() => {
            const origin = window.location.origin;
            const businessSlug = profileData.staff?.businessSlug;
            const staffSlug = profileData.staff.slug;
            const locationSlug =
              profileData.staff?.activeLocation?.slug ||
              profileData.staff?.activeLocationSlug;
            const role = profileData.staff?.role;
            const shareUrl =
              role === "R4" || role === "R7"
                ? `${origin}/freelancer/${staffSlug}`
                : businessSlug && locationSlug
                  ? `${origin}/business/${businessSlug}/${locationSlug}/professional/${staffSlug}`
                  : `${origin}/professional/${staffSlug}`;
            const name =
              `${profileData.user?.firstName || ""} ${profileData.user?.lastName || ""}`.trim();
            return (
              <div
                style={{
                  background: "#f0f6ff",
                  border: "1px solid #c3d9f5",
                  borderRadius: "10px",
                  padding: "16px 20px",
                  marginBottom: "24px",
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "flex-start" : "center",
                  gap: "12px",
                }}
              >
                <span style={{ fontSize: "20px" }}>🔗</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#001d4a",
                      marginBottom: "4px",
                    }}
                  >
                    Your Public Profile Link
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#2d6da3",
                      wordBreak: "break-all",
                    }}
                  >
                    {shareUrl}
                  </div>
                </div>
                <ShareProfileButton
                  url={shareUrl}
                  title={
                    name ? `${name} on Ogestyle` : "My profile on Ogestyle"
                  }
                />
              </div>
            );
          })()}

        {/* Main Content Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "350px 1fr",
            gap: "24px",
          }}
        >
          {/* Left Column - Profile Card */}
          <div
            className="dashboard-card"
            style={{
              padding: isMobile ? "24px" : "32px",
              height: "fit-content",
              position: isMobile ? "static" : "sticky",
              top: "24px",
            }}
          >
            {/* Avatar Section */}
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              {profileData.user.profilePicture ? (
                <img
                  src={profileData.user.profilePicture}
                  alt="Profile"
                  style={{
                    width: isMobile ? 120 : 160,
                    height: isMobile ? 120 : 160,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "5px solid #f0f4f8",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: isMobile ? 120 : 160,
                    height: isMobile ? 120 : 160,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: isMobile ? 48 : 64,
                    color: "#fff",
                    fontWeight: "bold",
                    border: "5px solid #f0f4f8",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  {profileData.user.firstName?.[0]}
                  {profileData.user.lastName?.[0]}
                </div>
              )}
              <h2
                style={{
                  margin: "20px 0 8px 0",
                  fontSize: isMobile ? "22px" : "26px",
                  color: "#111",
                }}
              >
                {profileData.user.firstName} {profileData.user.lastName}
              </h2>
              <div
                style={{
                  display: "inline-block",
                  padding: "8px 20px",
                  background:
                    "linear-gradient(135deg, #e6f2ff 0%, #d0e7ff 100%)",
                  color: "#1e40af",
                  borderRadius: "24px",
                  fontSize: "14px",
                  fontWeight: "600",
                  boxShadow: "0 2px 6px rgba(59, 130, 246, 0.15)",
                }}
              >
                {roleLabel}
              </div>
              {profileData.staff.rating > 0 && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px",
                    background: "#fef9e7",
                    borderRadius: "12px",
                    display: "inline-block",
                  }}
                >
                  <div style={{ fontSize: "24px", marginBottom: "4px" }}>
                    ⭐
                  </div>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "700",
                      color: "#111",
                    }}
                  >
                    {profileData.staff.rating.toFixed(1)}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>Rating</div>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div
              style={{
                borderTop: "1px solid #e5e7eb",
                paddingTop: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px",
                    background: "#f9fafb",
                    borderRadius: "8px",
                  }}
                >
                  <span style={{ fontSize: "20px" }}>📧</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        marginBottom: "2px",
                      }}
                    >
                      Email
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "500",
                        color: "#111",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {profileData.user.email}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px",
                    background: "#f9fafb",
                    borderRadius: "8px",
                  }}
                >
                  <span style={{ fontSize: "20px" }}>📱</span>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        marginBottom: "2px",
                      }}
                    >
                      Phone
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "500",
                        color: "#111",
                      }}
                    >
                      {profileData.user.phone || "Not provided"}
                    </div>
                  </div>
                </div>
                {profileData.staff.shop && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px",
                      background: "#f9fafb",
                      borderRadius: "8px",
                    }}
                  >
                    <span style={{ fontSize: "20px" }}>📍</span>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#666",
                          marginBottom: "2px",
                        }}
                      >
                        Location
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#111",
                        }}
                      >
                        {profileData.staff.shop.name}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        {profileData.staff.shop.city},{" "}
                        {profileData.staff.shop.state}
                      </div>
                    </div>
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px",
                    background: "#f9fafb",
                    borderRadius: "8px",
                  }}
                >
                  <span style={{ fontSize: "20px" }}>
                    {profileData.staff.status === "Active" ? "✅" : "⚠️"}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        marginBottom: "2px",
                      }}
                    >
                      Status
                    </div>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "4px 12px",
                        background:
                          profileData.staff.status === "Active"
                            ? "#d1fae5"
                            : "#fee2e2",
                        color:
                          profileData.staff.status === "Active"
                            ? "#065f46"
                            : "#991b1b",
                        borderRadius: "6px",
                        fontSize: "13px",
                        fontWeight: "600",
                      }}
                    >
                      {profileData.staff.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Information Sections */}
          <div
            style={{ display: "flex", flexDirection: "column", gap: "24px" }}
          >
            {/* Personal Information Card */}
            <div
              className="dashboard-card"
              style={{ padding: isMobile ? "24px" : "32px" }}
            >
              <h3
                style={{
                  margin: "0 0 20px 0",
                  color: "#111",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "28px" }}>👤</span>
                Personal Information
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: isMobile ? "16px" : "24px",
                }}
              >
                <div
                  style={{
                    padding: "16px",
                    background: "#f9fafb",
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      fontSize: "12px",
                      color: "#6b7280",
                      marginBottom: "6px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    First Name
                  </label>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "17px",
                      fontWeight: "600",
                      color: "#111",
                    }}
                  >
                    {profileData.user.firstName || "Not provided"}
                  </p>
                </div>
                <div
                  style={{
                    padding: "16px",
                    background: "#f9fafb",
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      fontSize: "12px",
                      color: "#6b7280",
                      marginBottom: "6px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Last Name
                  </label>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "17px",
                      fontWeight: "600",
                      color: "#111",
                    }}
                  >
                    {profileData.user.lastName || "Not provided"}
                  </p>
                </div>
              </div>
            </div>

            {/* Professional Information Card */}
            <div
              className="dashboard-card"
              style={{ padding: isMobile ? "24px" : "32px" }}
            >
              <h3
                style={{
                  margin: "0 0 20px 0",
                  color: "#111",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "28px" }}>💼</span>
                Professional Details
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: isMobile ? "16px" : "24px",
                }}
              >
                <div
                  style={{
                    padding: "16px",
                    background: "#f0f9ff",
                    borderRadius: "12px",
                    border: "1px solid #bfdbfe",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      fontSize: "12px",
                      color: "#1e40af",
                      marginBottom: "6px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Role
                  </label>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "17px",
                      fontWeight: "600",
                      color: "#111",
                    }}
                  >
                    {roleLabel}
                  </p>
                </div>
                {profileData.staff.shop && (
                  <>
                    <div
                      style={{
                        padding: "16px",
                        background: "#f0f9ff",
                        borderRadius: "12px",
                        border: "1px solid #bfdbfe",
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          fontSize: "12px",
                          color: "#1e40af",
                          marginBottom: "6px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Shop Name
                      </label>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "17px",
                          fontWeight: "600",
                          color: "#111",
                        }}
                      >
                        {profileData.staff.shop.name}
                      </p>
                    </div>
                    <div
                      style={{
                        padding: "16px",
                        background: "#f0f9ff",
                        borderRadius: "12px",
                        border: "1px solid #bfdbfe",
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          fontSize: "12px",
                          color: "#1e40af",
                          marginBottom: "6px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Location
                      </label>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "17px",
                          fontWeight: "600",
                          color: "#111",
                        }}
                      >
                        {profileData.staff.shop.city},{" "}
                        {profileData.staff.shop.state}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Services Section */}
            {services &&
              (services.templateServices.length > 0 ||
                services.professionalServices.length > 0) && (
                <div
                  className="dashboard-card"
                  style={{ padding: isMobile ? "24px" : "32px" }}
                >
                  <h3
                    style={{
                      margin: "0 0 24px 0",
                      color: "#111",
                      fontSize: isMobile ? "20px" : "24px",
                      fontWeight: "700",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span style={{ fontSize: "28px" }}>✂️</span>
                    My Services
                    <span
                      style={{
                        marginLeft: "auto",
                        padding: "6px 16px",
                        background:
                          "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        color: "white",
                        borderRadius: "20px",
                        fontSize: "14px",
                        fontWeight: "700",
                      }}
                    >
                      {services.totalCount} Total
                    </span>
                  </h3>

                  {/* Template Services */}
                  {services.templateServices.length > 0 && (
                    <div
                      style={{
                        marginBottom:
                          services.professionalServices.length > 0
                            ? "32px"
                            : "0",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "16px",
                          paddingBottom: "12px",
                          borderBottom: "2px solid #e5e7eb",
                        }}
                      >
                        <span style={{ fontSize: "20px" }}>🏢</span>
                        <h4
                          style={{
                            margin: 0,
                            fontSize: "16px",
                            color: "#374151",
                            fontWeight: "700",
                          }}
                        >
                          Business Services
                        </h4>
                        <span
                          style={{
                            marginLeft: "8px",
                            padding: "4px 12px",
                            background: "#f3f4f6",
                            color: "#6b7280",
                            borderRadius: "12px",
                            fontSize: "13px",
                            fontWeight: "600",
                          }}
                        >
                          {services.templateServices.length}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile
                            ? "1fr"
                            : "repeat(auto-fill, minmax(280px, 1fr))",
                          gap: "16px",
                        }}
                      >
                        {services.templateServices.map((service) => (
                          <div
                            key={service._id}
                            style={{
                              padding: "20px",
                              border: "2px solid #e5e7eb",
                              borderRadius: "12px",
                              background: "#ffffff",
                              transition: "all 0.2s",
                              cursor: "default",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = "#2d6da3";
                              e.currentTarget.style.boxShadow =
                                "0 4px 12px rgba(45, 109, 163, 0.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = "#e5e7eb";
                              e.currentTarget.style.boxShadow = "none";
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "start",
                                marginBottom: "12px",
                              }}
                            >
                              <h5
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  fontWeight: "700",
                                  color: "#111",
                                  lineHeight: "1.3",
                                }}
                              >
                                {service.name}
                              </h5>
                              {!service.isActive && (
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    background: "#fee2e2",
                                    color: "#991b1b",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "700",
                                  }}
                                >
                                  Inactive
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                                marginTop: "12px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <span style={{ fontSize: "16px" }}>⏱️</span>
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "#6b7280",
                                    fontWeight: "500",
                                  }}
                                >
                                  {service.durationMinutes} min
                                </span>
                              </div>
                              <div
                                style={{
                                  fontSize: "20px",
                                  fontWeight: "700",
                                  color: "#10b981",
                                  marginLeft: "auto",
                                }}
                              >
                                $
                                {service.price
                                  ? service.price.toFixed(2)
                                  : "0.00"}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Professional Services */}
                  {services.professionalServices.length > 0 && (
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "16px",
                          paddingBottom: "12px",
                          borderBottom: "2px solid #e5e7eb",
                        }}
                      >
                        <span style={{ fontSize: "20px" }}>⭐</span>
                        <h4
                          style={{
                            margin: 0,
                            fontSize: "16px",
                            color: "#374151",
                            fontWeight: "700",
                          }}
                        >
                          My Custom Services
                        </h4>
                        <span
                          style={{
                            marginLeft: "8px",
                            padding: "4px 12px",
                            background: "#dbeafe",
                            color: "#1e40af",
                            borderRadius: "12px",
                            fontSize: "13px",
                            fontWeight: "600",
                          }}
                        >
                          {services.professionalServices.length}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile
                            ? "1fr"
                            : "repeat(auto-fill, minmax(280px, 1fr))",
                          gap: "16px",
                        }}
                      >
                        {services.professionalServices.map((service) => (
                          <div
                            key={service._id}
                            style={{
                              padding: "20px",
                              border: "2px solid #bfdbfe",
                              borderRadius: "12px",
                              background:
                                "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
                              transition: "all 0.2s",
                              cursor: "default",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = "#3b82f6";
                              e.currentTarget.style.boxShadow =
                                "0 4px 12px rgba(59, 130, 246, 0.15)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = "#bfdbfe";
                              e.currentTarget.style.boxShadow = "none";
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "start",
                                marginBottom: "12px",
                              }}
                            >
                              <h5
                                style={{
                                  margin: 0,
                                  fontSize: "16px",
                                  fontWeight: "700",
                                  color: "#111",
                                  lineHeight: "1.3",
                                }}
                              >
                                {service.name}
                              </h5>
                              {!service.isActive && (
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    background: "#fee2e2",
                                    color: "#991b1b",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "700",
                                  }}
                                >
                                  Inactive
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                                marginTop: "12px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <span style={{ fontSize: "16px" }}>⏱️</span>
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "#1e40af",
                                    fontWeight: "600",
                                  }}
                                >
                                  {service.durationMinutes} min
                                </span>
                              </div>
                              <div
                                style={{
                                  fontSize: "20px",
                                  fontWeight: "700",
                                  color: "#059669",
                                  marginLeft: "auto",
                                }}
                              >
                                $
                                {service.price
                                  ? service.price.toFixed(2)
                                  : "0.00"}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>
      </div>

      <ServiceRequestModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        onSuccess={() => {
          // Refresh services to show pending request status
          fetchProfile();
          alert(
            "Service request submitted successfully! Your business owner will review it.",
          );
        }}
      />
    </StaffSidebarLayout>
  );
}

export default StaffProfile;
